import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStepsAtom } from '../../atoms';
import { APP_ROUTES, HSA_ONBOARD_STEPS } from '../../constants';
import { useIsFetching, useIsMutating, useMutation } from 'react-query';
import { useAtom } from 'jotai';
import { isDirtyAtom } from 'atoms/isDirtyAtom';
import {
  startSavingAtom,
  markSavedAtom,
  markSaveFailedAtom,
} from 'atoms/autoSaveStatusAtom';

// Components
import ConfirmationModal from '../../components/ConfirmationModal';

// Types
import { DirtyState, POSTErrorRes } from 'types';
import { onPreventEnterKeySubmit } from 'utils';
import { useIsRecalculatingHSA } from 'atoms/isRecalculatingHSA';
import { useDebounce } from 'hooks/useDebounce';

interface OnboardContainerProps {
  header?: React.ReactNode;
  children: React.ReactNode;
  isDisabled?: boolean;
  onSubmit: () => Promise<{ message: string }>;
  onValidate?: () => boolean;
  changeCount?: number;
  isValidInput?: boolean;
}

function OnboardContainer({
  header,
  children,
  onSubmit,
  isDisabled,
  onValidate,
  changeCount,
  isValidInput,
}: OnboardContainerProps) {
  const navigate = useNavigate();
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();

  const formRef = useRef<HTMLFormElement | null>(null);

  const [confirmPrev, setConfirmPrev] = useState(false);
  const { currentStep, incrementStep, decrementStep, setCurrentStep } =
    useOnboardingStepsAtom();
  const [isRecalculatingHSA] = useIsRecalculatingHSA();
  const [isDirty] = useAtom(isDirtyAtom);
  const [, startSaving] = useAtom(startSavingAtom);
  const [, markSaving] = useAtom(markSavedAtom);
  const [, markFailed] = useAtom(markSaveFailedAtom);

  const formDataChanged = isDirty[header as keyof DirtyState];

  const { mutateAsync } = useMutation({
    mutationFn: onSubmit,
    onError: (errRes: Error) => {
      let message = 'An unknown error occurred.';
      if (isAxiosError<POSTErrorRes>(errRes)) {
        message = errRes.response?.data.error.message || message;
      } else {
        message = errRes?.message || message;
      }
      toast.error(message);
    },
  });

  // const onClickSaveDraft = async () => {
  //   try {
  //     const isValid = onValidate
  //       ? onValidate()
  //       : formRef.current?.reportValidity();
  //     if (!isValid) return;

  //     await mutateAsync();
  //     // setCurrentStep(0);
  //     // navigate(APP_ROUTES.SETTINGS);
  //   } catch (err) {
  //     console.error(err);
  //   }
  // };

  const debouncedSave = useDebounce(async () => {
    const isValid = onValidate
      ? onValidate()
      : formRef.current?.reportValidity();

    if (!isValid) return;
    startSaving();

    try {
      await mutateAsync();
      markSaving();
    } catch (error) {
      markFailed();
    }
  }, 2500);

  // Handle auto saving of the edited HSA draft/ Recalculate - only if input fields are valid
  useEffect(() => {
    if (!isRecalculatingHSA) return;
    if (!changeCount) return;

    if (formDataChanged && isRecalculatingHSA && isValidInput) {
      debouncedSave();
    } else {
      debouncedSave.cancel(); // Cancel pending debounced save to avoid sending request with stale data
    }
  }, [
    changeCount,
    debouncedSave,
    header,
    formDataChanged,
    isRecalculatingHSA,
    isValidInput,
  ]);

  const onClickPrevious = () => {
    debouncedSave.cancel();
    setConfirmPrev(true);
  };

  const onClickNext = async () => {
    try {
      debouncedSave.cancel();
      const isValid = onValidate
        ? onValidate()
        : formRef.current?.reportValidity();
      if (!isValid) return;
      // Mutate only if any form inputs have changed for the specific form (step)
      if (formDataChanged) {
        await mutateAsync();
      }

      incrementStep();
    } catch (err) {
      console.error(err);
    }
  };

  const onConfirmPrev = () => {
    decrementStep();
    setConfirmPrev(false);
  };

  // JSX
  return (
    <>
      <div className="">
        <div className={isMutating || isFetching ? 'cursor-wait h-full' : ''}>
          <h3 className="font-bold text-2xl">
            {/*The (Optional) is not directly added to the HSAWhitelistedForm Onboarding header as header values are used as keys in other logic (isDirtyAtom)*/}
            {header === 'Whitelisted Domains'
              ? `${header} (Optional)`
              : header}{' '}
          </h3>
          <form
            className="mt-6 space-y-8"
            ref={formRef}
            onKeyDown={onPreventEnterKeySubmit}
          >
            {children}
          </form>
        </div>
      </div>
      <div className="flex justify-end pt-4">
        {/*<button
          className={`text-center leading-none text-sm font-bold py-2 px-6 w-[150px] h-[34px] text-gray-700 border-2 border-solid border-gray500 rounded-[20px] disabled:hover:cursor-not-allowed disabled:opacity-60 ${isFetching ? 'hover:cursor-wait' : ''
            }`}
          onClick={onClickSaveDraft}
          disabled={!!isFetching || !!isMutating || isDisabled}
        >
          Save as Draft
        </button>*/}
        <div className="flex gap-4">
          {currentStep > 0 ? (
            <button
              type="button"
              className={`text-center leading-none text-sm font-bold py-2 px-6 w-[150px] h-[34px] text-light bg-gray500 rounded-[20px] disabled:cursor-not-allowed disabled:opacity-60 ${
                isFetching ? 'hover:cursor-wait disabled:cursor-wait' : ''
              }`}
              // Skip showing confirmation toast (ConfirmationModal) when recalculating as the changes will already be saved as draft, isDirty flag is used to check for unsaved changes
              onClick={
                isRecalculatingHSA && !formDataChanged
                  ? onConfirmPrev
                  : onClickPrevious
              }
              disabled={!!isFetching || !!isMutating || currentStep === 2} // Disable Previous when on Properties page  (currentStep === 2)
            >
              Previous
            </button>
          ) : null}
          <button
            className={`flex items-center justify-center text-sm font-bold py-2 px-6 w-[150px] h-[34px] bg-success text-light rounded-[20px] disabled:cursor-not-allowed disabled:opacity-60 ${
              isFetching ? 'hover:cursor-wait disabled:cursor-wait' : ''
            }`}
            onClick={onClickNext}
            disabled={!!isFetching || !!isMutating || isDisabled}
          >
            {currentStep === HSA_ONBOARD_STEPS.length ? 'Initiate' : 'Next'}
          </button>
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmPrev}
        content={
          <div className="text-center px-4">
            <p>Any unsaved changes will be discarded.</p>
            <p>Are you sure you want to continue?</p>
          </div>
        }
        onCancel={() => setConfirmPrev(false)}
        onConfirm={onConfirmPrev}
      />
    </>
  );
}

export default OnboardContainer;
